import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DurationBar } from '../../components/charts';
import type { OrderStatus } from '../../types/Order';
import { formatSeconds } from './format';
import type { OrderTiming } from './types';

const STATES = ['pending', 'accepted', 'preparing', 'ready'] as const;
const isTimed = (s: OrderStatus): s is (typeof STATES)[number] => (STATES as readonly string[]).includes(s);

export interface TimeInStateProps {
    timing: OrderTiming;
    status: OrderStatus;
    /** Browser time (ms) at which `timing` arrived; the running state counts on from here. */
    receivedAt: number;
}

/** Total time per state as one bar, plus the total. The current state keeps ticking. */
export const TimeInState = ({ timing, status, receivedAt }: TimeInStateProps) => {
    const { t } = useTranslation(['orderDetail', 'common']);
    const running = isTimed(status);
    const [now, setNow] = useState(Date.now);
    useEffect(() => {
        if (!running) return;
        const h = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(h);
    }, [running]);

    const extra = running ? Math.max(0, now - receivedAt) / 1000 : 0;
    const seconds = STATES.map((s) => timing.seconds[s] + (s === status ? extra : 0));
    const total = seconds.reduce((a, b) => a + b, 0);
    const time = formatSeconds(status === 'completed' && timing.toComplete !== null ? timing.toComplete : total, t);
    const summary =
        status === 'completed' ? 'completedIn' : status === 'cancelled' ? 'cancelledAfter' : 'open';

    return (
        <div>
            <p className="mb-3 text-small text-body">
                {t(`orderDetail:summary.${summary}`, { time })}
            </p>
            <DurationBar
                caption={t('orderDetail:summary.caption')}
                labelHeading={t('orderDetail:summary.stateHeading')}
                durationLabel={t('orderDetail:summary.durationHeading')}
                segments={STATES.map((s, i) => ({ key: s, label: t(`common:status.${s}`), minutes: (seconds[i] ?? 0) / 60 }))}
                formatValue={(minutes) => formatSeconds(minutes * 60, t)}
            />
            {timing.estimated && <p className="mt-2 text-caption text-muted">{t('orderDetail:summary.estimated')}</p>}
        </div>
    );
};
